import { useCallback, useEffect, useState } from 'react';

import { CILogger } from 'common/log/log.provider';

/** Flows that may only run in one browser tab at a time. */
export type BrowserTabFlowName = 'deviceHandoff';

export type BrowserTabFlowOwnership = 'checking' | 'ownedByThisTab' | 'ownedByAnotherTab';

const ownershipPerFlow = new Map<BrowserTabFlowName, BrowserTabFlowOwnership>();

const listenersPerFlow = new Map<BrowserTabFlowName, Set<() => void>>();

function publishOwnership(flowName: BrowserTabFlowName, ownership: BrowserTabFlowOwnership): void {
    ownershipPerFlow.set(flowName, ownership);
    listenersPerFlow.get(flowName)?.forEach((notify) => notify());
}

function supportsWebLocks(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.locks;
}

let missingWebLocksReported = false;

function ownershipWithoutWebLocks(flowName: BrowserTabFlowName): BrowserTabFlowOwnership {
    if (!missingWebLocksReported) {
        missingWebLocksReported = true;
        CILogger.getInstance().warn(
            `browser tab flow owner: this browser has no Web Locks, so the ${flowName} flow is not confined to one tab`,
            null
        );
    }

    publishOwnership(flowName, 'ownedByThisTab');

    return 'ownedByThisTab';
}

function keepHoldingUntilStolen(): Promise<never> {
    return new Promise<never>(() => undefined);
}

function holdFlowLock(flowName: BrowserTabFlowName, options: LockOptions): void {
    navigator.locks
        .request(flowName, options, async (lock) => {
            if (!lock) {
                publishOwnership(flowName, 'ownedByAnotherTab');

                return;
            }

            publishOwnership(flowName, 'ownedByThisTab');

            await keepHoldingUntilStolen();
        })
        .catch(() => publishOwnership(flowName, 'ownedByAnotherTab'));
}

export function browserTabFlowOwnership(flowName: BrowserTabFlowName): BrowserTabFlowOwnership {
    const known = ownershipPerFlow.get(flowName);

    if (known) {
        return known;
    }

    if (!supportsWebLocks()) {
        return ownershipWithoutWebLocks(flowName);
    }

    publishOwnership(flowName, 'checking');
    holdFlowLock(flowName, { ifAvailable: true });

    return ownershipPerFlow.get(flowName) ?? 'checking';
}

export function takeOverFlowInThisTab(flowName: BrowserTabFlowName): void {
    if (!supportsWebLocks()) {
        ownershipWithoutWebLocks(flowName);

        return;
    }

    holdFlowLock(flowName, { steal: true });
    publishOwnership(flowName, 'ownedByThisTab');
}

export function isFlowOwnedByThisTab(flowName: BrowserTabFlowName): boolean {
    return browserTabFlowOwnership(flowName) === 'ownedByThisTab';
}

export function useBrowserTabFlowOwner(flowName: BrowserTabFlowName): {
    flowOwnership: BrowserTabFlowOwnership;
    takeOverFlowInThisTab: () => void;
} {
    const [flowOwnership, setFlowOwnership] = useState(() => browserTabFlowOwnership(flowName));

    useEffect(() => {
        const syncOwnership = (): void => setFlowOwnership(browserTabFlowOwnership(flowName));
        const listeners = listenersPerFlow.get(flowName) ?? new Set<() => void>();

        listeners.add(syncOwnership);
        listenersPerFlow.set(flowName, listeners);

        syncOwnership();

        return () => {
            listeners.delete(syncOwnership);
        };
    }, [flowName]);

    return {
        flowOwnership,
        takeOverFlowInThisTab: useCallback(() => takeOverFlowInThisTab(flowName), [flowName]),
    };
}
